import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CapitalCtx = createContext(null);

const STORAGE_KEY = "gbe_capital_client";
const REGISTER_FILTER_KEY = "gbe_asset_register_filters_v1";

// Two-way sync helper: when the top-right client dropdown changes, also
// scope the Asset Register / Portfolio View filter (single-client array) so
// every tab in Capital Planning stays in sync.
function _syncRegisterFilterToClient(name) {
  try {
    const raw = localStorage.getItem(REGISTER_FILTER_KEY);
    const cur = raw ? JSON.parse(raw) : {};
    const next = { ...cur, clients: name ? [name] : [] };
    localStorage.setItem(REGISTER_FILTER_KEY, JSON.stringify(next));
    // Trigger listeners inside the same window (`storage` event only fires
    // across windows/tabs; use a custom event for same-window components).
    window.dispatchEvent(new CustomEvent("gbe:capital-filters-changed",
                                          { detail: next }));
  } catch {}
}

export function CapitalProvider({ children }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/capital/clients");
      setClients(data);
      const saved = localStorage.getItem(STORAGE_KEY);
      const match = saved && data.find((c) => c.name === saved);
      const initial = match ? match.name : (data[0]?.name || null);
      setSelected(initial);
      // Ensure Register/Portfolio filter matches the initial client
      _syncRegisterFilterToClient(initial);
    } catch (e) {
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        console.warn("[CapitalContext] failed to load clients", e?.message || e);
      }
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const setClient = (name) => {
    setSelected(name);
    if (name) localStorage.setItem(STORAGE_KEY, name);
    else localStorage.removeItem(STORAGE_KEY);
    _syncRegisterFilterToClient(name);
  };

  // Listen for Asset Register / Portfolio filter changes and reflect back
  // to the top-right selector when the user picks exactly one client there.
  useEffect(() => {
    const onFiltersChanged = (e) => {
      const picked = e.detail?.clients || [];
      if (picked.length === 1 && picked[0] !== selected) {
        setSelected(picked[0]);
        try { localStorage.setItem(STORAGE_KEY, picked[0]); } catch {}
      } else if (picked.length === 0 && selected) {
        // Cleared filter on Register → don't clear top-right (Overview tab
        // needs a single client to stay functional). No-op.
      }
    };
    window.addEventListener("gbe:capital-filters-changed", onFiltersChanged);
    return () => window.removeEventListener("gbe:capital-filters-changed", onFiltersChanged);
  }, [selected]);

  return (
    <CapitalCtx.Provider value={{ clients, client: selected, setClient, loading, refresh: load }}>
      {children}
    </CapitalCtx.Provider>
  );
}

export const useCapital = () => useContext(CapitalCtx);
